import { useState } from 'react';
import {
    useFloating,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    FloatingFocusManager,
    offset,
    flip,
    autoUpdate,
} from '@floating-ui/react';
import { cn } from 'Utils/cn';
import PropTypes from 'prop-types';
import OutlineChevronDown from 'Components/Atoms/OutlineIcons/OutlineChevronDown';
import { AnimatePresence, motion } from 'framer-motion';
import selectVariants from 'Components/Atoms/Select/SelectVariants';
import FloatLabel from '../FloatLabel/FloatLabel';
import ErrorMessage from '../ErrorMessage/ErrorMessage';

export default function ColorPicker(props) {
    const { className, value, onChange, colors, error, label, disabled } = props;

    const [isOpen, setIsOpen] = useState(false);
    const { refs, context, strategy, x, y } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        whileElementsMounted: autoUpdate,
        placement: 'bottom-start',
        middleware: [offset(4), flip()],
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'listbox' }),
        useDismiss(context),
    ]);

    return (
        <div className="relative min-w-20">
            <FloatLabel label={label} isOpen={isOpen} />

            <button
                ref={refs.setReference}
                disabled={disabled}
                type="button"
                className={cn(selectVariants({ className }), isOpen && 'border-primary ring-2 ring-primary/20')}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                {...getReferenceProps()}
            >
                <div
                    className={`size-6 rounded-full bg-[var(--color-picker-value)] shrink-0`}
                    style={{ '--color-picker-value': value }}
                />
                <OutlineChevronDown size={20} className="shrink-0 text-dark-blue" color="currentColor" />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <FloatingFocusManager context={context}>
                        <motion.div
                            ref={refs.setFloating}
                            style={{
                                position: strategy,
                                top: y ?? 0,
                                left: x ?? 0,
                            }}
                            className="mt-1 w-full rounded-md bg-white shadow-sm ring-1 ring-black/5 focus:outline-none text-sm z-dropdown min-w-3xs"
                            tabIndex={-1}
                            {...getFloatingProps()}
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.15, ease: 'easeOut' }}
                        >
                            <div className="px-4 py-5 flex gap-4 flex-wrap">
                                {colors.map((color) => (
                                    <button
                                        key={color}
                                        type="button"
                                        className={cn(`size-6 rounded-full bg-[var(--color-picker-value)]`, {
                                            [`ring-2 ring-[var(--color-picker-value)] ring-offset-2`]: color === value,
                                        })}
                                        style={{ '--color-picker-value': color }}
                                        onClick={() => onChange(color)}
                                    />
                                ))}
                            </div>
                        </motion.div>
                    </FloatingFocusManager>
                )}
            </AnimatePresence>

            <ErrorMessage message={error} />
        </div>
    );
}

ColorPicker.propTypes = {
    className: PropTypes.string,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    colors: PropTypes.arrayOf(PropTypes.string),
    error: PropTypes.string,
    label: PropTypes.string,
    disabled: PropTypes.bool,
};
